/**
 * Curated message shown to the client when an agent has used
 * REQUEST_INFO on their onboarding case. Displays only the structured
 * `{summary, issues[]}` that the backend exposes as
 * `caseDetail.latest_client_message` — internal staff notes are NEVER
 * surfaced (the privacy gate in workflow.py enforces that boundary).
 *
 * The component is intentionally action-less: the client's next step
 * is to scroll down to the form below the banner and fix / re-upload
 * what the bullets describe, then click Resubmit. We chose NOT to add
 * a CTA button so the client can't dismiss the bullets without
 * actually addressing them.
 */
import { colors } from '../../styles/theme';


export interface KycClientMessage {
  summary: string;
  issues: string[];
}

interface Props {
  message: KycClientMessage;
}

export default function KycInfoRequestBanner({ message }: Readonly<Props>): JSX.Element {
  return (
    <aside
      role="status"
      aria-live="polite"
      className="rounded-2xl p-5 mb-6"
      style={{
        background: colors.warningBg,
        border: `1px solid #FCD34D`,
      }}
    >
      <h2
        className="text-sm font-semibold mb-2 flex items-center gap-2"
        style={{ color: colors.warning }}
      >
        <span aria-hidden="true">✎</span>
        {/* */}
        Our team needs a few corrections before we can approve
      </h2>
      <p className="text-sm mb-3" style={{ color: colors.ink }}>
        {message.summary}
      </p>
      {message.issues.length > 0 && (
        <ul
          className="text-sm list-disc pl-5 space-y-1"
          style={{ color: colors.inkSoft }}
        >
          {message.issues.map((issue, idx) => (
            <li key={`${idx}-${issue.slice(0, 24)}`}>{issue}</li>
          ))}
        </ul>
      )}
      <p className="text-xs mt-3" style={{ color: colors.muted }}>
        Update the affected fields or re-upload documents below, then click
        {/* */}
        <strong> Resubmit </strong>
        {/* */}
        to send your case back for review.
      </p>
    </aside>
  );
}
